import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { SecondaryNav, TablePage } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import {
  ALLOCATION_COUNT_CAP,
  isAllocationTypeHidden,
  useAllocationTypes,
} from "hooks/useAllocationTypes";
import type { AllocationType } from "hooks/useAllocationTypes";
import Role from "enums/Role";
import AllocationEditTypeEnum from "../../enums/AllocationEditType";
import AllocationTable from "../../components/admin/allocations/AllocationTable";
import type { AllocationData } from "../../components/admin/allocations/AllocationTable";
import AllocationDrawer from "../../components/admin/allocations/AllocationDrawer";
import AllocationTypeControls, {
  isSyncedType,
} from "../../components/admin/allocations/AllocationTypeControls";
import AllocationTypeVisibilityToggle from "../../components/admin/allocations/AllocationTypeVisibilityToggle";

export type AllocationFilterCriteriaProps = {
  searchTerm: string;
};

/**
 * Allocations, one tab per allocation type. Each tab is a full-height table on
 * the page's own scroll, and the type's sync, custom field and visibility sit
 * beside the search rather than in a separate settings drawer.
 */
const Allocations: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Allocations"));

  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [activeTypeKey, setActiveTypeKey] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [allocationToChange, setAllocationToChange] = useState<
    AllocationData | undefined
  >(undefined);
  const [allocationTypeToChange, setAllocationTypeToChange] = useState<
    { key: string; name: string } | undefined
  >(undefined);
  const [editType, setEditType] = useState<AllocationEditTypeEnum | undefined>(
    undefined,
  );

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAdmin = useCheckUserRole(Role.Admin);
  const canManage = !isAuditor;

  const {
    allocationTypes,
    loading: loadingTypes,
    refetchAllocationTypes,
  } = useAllocationTypes({
    showAll: true,
    withCounts: true,
  });

  // Opens on the first type that has allocations, so an empty type is not the landing view.
  useEffect(() => {
    if (
      allocationTypes.length > 0 &&
      !allocationTypes.some((type) => type.key === activeTypeKey)
    ) {
      const firstWithAllocations = allocationTypes.find(
        (type) => (type.allocationCount ?? 0) > 0,
      );
      setActiveTypeKey((firstWithAllocations ?? allocationTypes[0]).key);
    }
  }, [allocationTypes, activeTypeKey]);

  const activeType: AllocationType | undefined = useMemo(
    () => allocationTypes.find((type) => type.key === activeTypeKey),
    [allocationTypes, activeTypeKey],
  );

  // Separators off: the name is tenant data.
  const nameOf = (type: AllocationType) =>
    t(type.name, { nsSeparator: false, keySeparator: false });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const navLabel = (type: AllocationType) => {
    const count = type.allocationCount ?? 0;
    const values = {
      name: nameOf(type),
      count: count > ALLOCATION_COUNT_CAP ? `${ALLOCATION_COUNT_CAP}+` : count,
    };
    return isAllocationTypeHidden(type)
      ? t("AllocationTypeHiddenNavLabel", values)
      : t("AllocationTypeNavLabel", values);
  };

  const typeItems = allocationTypes.map((type) => ({
    id: type.key,
    label: navLabel(type),
  }));

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setAllocationToChange(undefined);
    setEditType(undefined);
  };

  const handleOpenAllocationDrawer = (
    nextEditType?: AllocationEditTypeEnum,
    allocation?: AllocationData,
    type?: { key: string; name: string },
  ) => {
    setEditType(nextEditType);
    setAllocationToChange(allocation);
    setAllocationTypeToChange(type);
    setOpenDrawer(true);
  };

  // A synced type takes its allocations from the ledger, so adding opens on a type managed here.
  const addAllocation = () =>
    activeType && !isSyncedType(activeType)
      ? handleOpenAllocationDrawer(
          AllocationEditTypeEnum.NEW_WITH_TYPE,
          undefined,
          {
            key: activeType.key,
            name: activeType.name,
          },
        )
      : handleOpenAllocationDrawer(AllocationEditTypeEnum.NEW);

  const refreshTypes = () => {
    void refetchAllocationTypes();
    setRefreshKey((key) => key + 1);
  };

  return (
    <TablePage
      title={t("Allocations")}
      breadcrumbs={breadcrumbs}
      // TablePage gives its filter slot no width, so the slot grows to let the type markers sit at the far right.
      sx={{ "& div:has(> [data-type-controls])": { flex: 1 } }}
      primaryAction={
        canManage && (
          <Button variant="primary-filled" onClick={addAllocation}>
            {t("Add Allocation")}
          </Button>
        )
      }
      search={search}
      filters={
        activeType && <AllocationTypeControls allocationType={activeType} />
      }
    >
      {typeItems.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            flex: 1,
            minHeight: 0,
            gridTemplateColumns: isSmallScreen
              ? "minmax(0, 1fr)"
              : "220px minmax(0, 1fr)",
            gridTemplateRows: isSmallScreen
              ? "auto minmax(0, 1fr)"
              : "minmax(0, 1fr)",
            "& > *": { minHeight: 0, overflowY: "auto" },
          }}
        >
          <SecondaryNav
            variant="inline"
            orientation={isSmallScreen ? "horizontal" : "vertical"}
            ariaLabel={t("AllocationTypes")}
            items={typeItems}
            activeId={activeTypeKey}
            onSelect={setActiveTypeKey}
          />
          <AllocationTable
            allocationType={
              activeType ? { key: activeType.key, name: activeType.name } : null
            }
            searchTerm={searchTerm}
            refreshKey={refreshKey}
            canManage={canManage}
            typeHidden={!!activeType && isAllocationTypeHidden(activeType)}
            typeVisibilityToggle={
              isAdmin &&
              activeType && (
                <AllocationTypeVisibilityToggle
                  allocationType={activeType}
                  onChanged={refreshTypes}
                />
              )
            }
            onDeleted={() => void refetchAllocationTypes()}
            handleOpenAllocationDrawer={handleOpenAllocationDrawer}
          />
        </Box>
      ) : (
        !loadingTypes && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--space-8)",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("No allocations found")}
            </Typography>
          </Box>
        )
      )}

      <AllocationDrawer
        open={openDrawer}
        handleCloseDrawer={handleCloseDrawer}
        afterAllocationSubmit={() => {
          handleCloseDrawer();
          refreshTypes();
        }}
        allocationToChange={allocationToChange}
        allocationTypeToChange={allocationTypeToChange}
        editType={editType}
      />
    </TablePage>
  );
};

export default Allocations;
